package uz.mint.frontend.api

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue

/** What the storefront knows about who is looking at it. */
sealed interface SessionState {
    /** Before the first `/api/auth/me` answers. Distinct from [Anonymous] on purpose. */
    data object Loading : SessionState

    data object Anonymous : SessionState

    data class SignedIn(val user: Customer) : SessionState
}

/**
 * Who is signed in, shared across the page.
 *
 * A single mutable object rather than state hoisted through composables: the header, the login
 * dialog and the account pages all need the same answer, and they are not in one another's
 * composition tree. There is exactly one browser tab and one customer in it, so a singleton is
 * the honest shape.
 *
 * **[SessionState.Loading] is not [SessionState.Anonymous].** The pages are statically exported
 * and render before the session is known; treating "not yet asked" as "logged out" would bounce
 * a signed-in customer to the login screen on every page load.
 */
object SessionStore {

    var state by mutableStateOf<SessionState>(SessionState.Loading)
        private set

    private var asked = false

    /** Resolves the session once per page load; repeat calls are free. */
    suspend fun ensureLoaded() {
        if (asked) return
        asked = true
        refresh()
    }

    suspend fun refresh() {
        state = AuthApi.currentUser()
            ?.let { SessionState.SignedIn(it) }
            ?: SessionState.Anonymous
    }

    /** Called after a successful code verification, which already returned the customer. */
    fun signedIn(user: Customer) {
        asked = true
        state = SessionState.SignedIn(user)
    }

    fun updated(user: Customer) {
        if (state is SessionState.SignedIn) state = SessionState.SignedIn(user)
    }

    suspend fun signOut() {
        AuthApi.logout()
        state = SessionState.Anonymous
    }
}

/** Reads the shared session, kicking off the initial lookup on first use. */
@Composable
fun rememberSession(): SessionState {
    LaunchedEffect(Unit) { SessionStore.ensureLoaded() }
    return SessionStore.state
}
